import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Utensils, Clock, Plus, Minus, Trash2, Save, Share2, Printer, Check, ChevronDown, ChevronUp, Copy, ArrowRight, X, ShoppingCart, Beef, Apple, Flame, Edit3, BookOpen, LogIn, Sparkles, Target, Loader2, TrendingUp } from 'lucide-react';
import { mealPlansApi } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SLOTS = [
  { id: 'breakfast', label: 'Breakfast', color: '#f59e0b' },
  { id: 'lunch', label: 'Lunch', color: '#22c55e' },
  { id: 'dinner', label: 'Dinner', color: '#E63946' },
  { id: 'snack', label: 'Snack', color: '#8b5cf6' },
];

function RecipePicker({ recipes, customRecipes, onSelect, onClose, slot }) {
  const [filter, setFilter] = useState('All');
  const allRecipes = [...recipes, ...customRecipes];
  const categories = ['All', ...new Set(allRecipes.map(r => r.category))];
  const filtered = filter === 'All' ? allRecipes : allRecipes.filter(r => r.category === filter);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="recipe-picker-overlay">
      <div className="bg-[#111] border border-zinc-800 w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="font-heading text-lg text-white uppercase tracking-wider">Pick a Recipe</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1 p-3 overflow-x-auto scrollbar-hide border-b border-zinc-800/50">
          {categories.map(c => (
            <button key={c} onClick={() => setFilter(c)}
              className={`px-3 py-1 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap transition-all ${filter === c ? 'bg-[#22c55e] text-black font-bold' : 'text-zinc-500 hover:text-zinc-300 border border-zinc-800'}`}
              data-testid={`picker-filter-${c.toLowerCase()}`}>{c}</button>
          ))}
        </div>
        <div className="overflow-y-auto max-h-[50vh] p-3 space-y-2">
          <button onClick={() => { onSelect(null); onClose(); }}
            className="w-full flex items-center gap-3 p-3 border border-zinc-800/50 text-zinc-500 hover:border-zinc-600 transition-all text-left"
            data-testid="picker-clear">
            <X className="w-4 h-4" /> <span className="text-sm">Clear this slot</span>
          </button>
          {filtered.map(r => (
            <button key={r.id} onClick={() => { onSelect(r); onClose(); }}
              className="w-full flex items-center gap-3 p-3 border border-zinc-800/50 hover:border-zinc-600 transition-all text-left"
              data-testid={`picker-recipe-${r.id}`}>
              <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{r.name}</p>
                <div className="flex gap-3 text-[10px] text-zinc-500">
                  <span>{r.protein}g protein</span>
                  <span>{r.calories} cal</span>
                  <span>{r.time}</span>
                </div>
              </div>
              {r._custom && <span className="text-[9px] text-zinc-600 border border-zinc-800 px-1.5 py-0.5">CUSTOM</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddRecipeModal({ onAdd, onClose }) {
  const [form, setForm] = useState({ name: '', protein: '', calories: '', time: '15 min', category: 'Lunch', ingredients: '', steps: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const CATS = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
  const COLORS = { Breakfast: '#f59e0b', Lunch: '#22c55e', Dinner: '#E63946', Snack: '#8b5cf6' };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onAdd({
      id: `custom-${Date.now()}`,
      name: form.name.trim(),
      protein: parseInt(form.protein) || 0,
      calories: parseInt(form.calories) || 0,
      time: form.time || '15 min',
      category: form.category,
      color: COLORS[form.category] || '#22c55e',
      servings_base: 1,
      ingredients: form.ingredients.split('\n').filter(Boolean),
      steps: form.steps.split('\n').filter(Boolean),
      _custom: true,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#111] border border-zinc-800 w-full max-w-md p-5" onClick={e => e.stopPropagation()} data-testid="add-recipe-modal">
        <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4">Add Custom Recipe</h3>
        <div className="space-y-3">
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Recipe name" className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2.5 focus:border-[#22c55e] outline-none" data-testid="custom-recipe-name" />
          <div className="grid grid-cols-3 gap-2">
            <input value={form.protein} onChange={e => set('protein', e.target.value)} placeholder="Protein (g)" type="number" className="bg-zinc-900 border border-zinc-800 text-white text-sm p-2.5 outline-none focus:border-[#22c55e]" data-testid="custom-recipe-protein" />
            <input value={form.calories} onChange={e => set('calories', e.target.value)} placeholder="Calories" type="number" className="bg-zinc-900 border border-zinc-800 text-white text-sm p-2.5 outline-none focus:border-[#22c55e]" data-testid="custom-recipe-calories" />
            <input value={form.time} onChange={e => set('time', e.target.value)} placeholder="Time" className="bg-zinc-900 border border-zinc-800 text-white text-sm p-2.5 outline-none focus:border-[#22c55e]" data-testid="custom-recipe-time" />
          </div>
          <div className="flex gap-1">
            {CATS.map(c => (
              <button key={c} onClick={() => set('category', c)}
                className={`px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider border transition-all ${form.category === c ? 'bg-zinc-700 text-white border-zinc-600' : 'text-zinc-500 border-zinc-800'}`}>{c}</button>
            ))}
          </div>
          <textarea value={form.ingredients} onChange={e => set('ingredients', e.target.value)} placeholder="Ingredients (one per line)" rows={3} className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2.5 outline-none focus:border-[#22c55e] resize-none" data-testid="custom-recipe-ingredients" />
          <textarea value={form.steps} onChange={e => set('steps', e.target.value)} placeholder="Steps (one per line)" rows={3} className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm p-2.5 outline-none focus:border-[#22c55e] resize-none" data-testid="custom-recipe-steps" />
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleSubmit} className="flex-1 py-2.5 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors" data-testid="custom-recipe-save">Add Recipe</button>
          <button onClick={onClose} className="px-4 py-2.5 border border-zinc-800 text-zinc-500 text-xs font-heading uppercase tracking-wider hover:text-white transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AiGenerateModal({ onGenerate, onClose }) {
  const [goal, setGoal] = useState('maintain');
  const [calories, setCalories] = useState(2000);
  const [restrictions, setRestrictions] = useState([]);
  const [useExisting, setUseExisting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const GOALS = [
    { id: 'bulk', label: 'Bulk', desc: 'High cal, muscle gain', color: '#3b82f6', split: '30P / 45C / 25F' },
    { id: 'maintain', label: 'Maintain', desc: 'Balanced macros', color: '#22c55e', split: '30P / 40C / 30F' },
    { id: 'cut', label: 'Cut', desc: 'High protein, deficit', color: '#E63946', split: '40P / 30C / 30F' },
  ];
  const RESTRICTIONS = ['Vegetarian', 'Vegan', 'Dairy-Free', 'Gluten-Free', 'Nut-Free', 'Low-Carb', 'Keto', 'Pescatarian'];

  const toggleRestriction = (r) => {
    setRestrictions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const { data } = await mealPlansApi.aiGenerate({
        goal,
        calories: parseInt(calories),
        restrictions,
        use_existing_recipes: useExisting,
      });
      onGenerate(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="ai-generate-overlay">
      <div className="bg-[#111] border border-zinc-800 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="ai-generate-modal">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#22c55e]" />
            <h3 className="font-heading text-lg text-white uppercase tracking-wider">AI Meal Plan</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Goal */}
          <div>
            <p className="text-xs font-heading uppercase tracking-widest text-zinc-500 mb-3">Fitness Goal</p>
            <div className="grid grid-cols-3 gap-2">
              {GOALS.map(g => (
                <button key={g.id} onClick={() => setGoal(g.id)}
                  className={`p-3 border text-left transition-all ${goal === g.id ? 'border-zinc-600 bg-zinc-800/60' : 'border-zinc-800/50 hover:border-zinc-700'}`}
                  data-testid={`goal-${g.id}`}>
                  <p className="text-sm font-bold text-white mb-0.5">{g.label}</p>
                  <p className="text-[10px] text-zinc-500">{g.desc}</p>
                  <p className="text-[9px] font-mono mt-1" style={{ color: g.color }}>{g.split}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Calories */}
          <div>
            <p className="text-xs font-heading uppercase tracking-widest text-zinc-500 mb-3">Daily Calories</p>
            <div className="flex items-center gap-3">
              <input type="range" min="1200" max="4000" step="50" value={calories}
                onChange={e => setCalories(e.target.value)}
                className="flex-1 accent-[#22c55e] h-1.5 bg-zinc-800 rounded-full appearance-none cursor-pointer"
                data-testid="calories-slider" />
              <div className="border border-zinc-800 px-3 py-1.5 min-w-[80px] text-center">
                <input type="number" value={calories} onChange={e => setCalories(e.target.value)}
                  className="bg-transparent text-white text-sm font-heading w-14 text-center outline-none"
                  data-testid="calories-input" />
                <span className="text-[10px] text-zinc-500 ml-1">cal</span>
              </div>
            </div>
            <div className="flex justify-between text-[10px] text-zinc-600 mt-1 font-mono">
              <span>1,200</span><span>2,000</span><span>3,000</span><span>4,000</span>
            </div>
          </div>

          {/* Restrictions */}
          <div>
            <p className="text-xs font-heading uppercase tracking-widest text-zinc-500 mb-3">Dietary Restrictions <span className="text-zinc-700">(optional)</span></p>
            <div className="flex flex-wrap gap-1.5">
              {RESTRICTIONS.map(r => (
                <button key={r} onClick={() => toggleRestriction(r)}
                  className={`px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider border transition-all ${restrictions.includes(r) ? 'border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e]' : 'border-zinc-800 text-zinc-600 hover:text-zinc-400'}`}
                  data-testid={`restriction-${r.toLowerCase()}`}>{r}</button>
              ))}
            </div>
          </div>

          {/* Recipe Source */}
          <div>
            <p className="text-xs font-heading uppercase tracking-widest text-zinc-500 mb-3">Recipe Source</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setUseExisting(false)}
                className={`p-3 border text-left transition-all ${!useExisting ? 'border-[#22c55e] bg-[#22c55e]/5' : 'border-zinc-800/50 hover:border-zinc-700'}`}
                data-testid="source-ai-new">
                <p className="text-sm font-bold text-white mb-0.5">AI Creates New</p>
                <p className="text-[10px] text-zinc-500">Brand new recipes tailored to your goals</p>
              </button>
              <button onClick={() => setUseExisting(true)}
                className={`p-3 border text-left transition-all ${useExisting ? 'border-[#22c55e] bg-[#22c55e]/5' : 'border-zinc-800/50 hover:border-zinc-700'}`}
                data-testid="source-existing">
                <p className="text-sm font-bold text-white mb-0.5">Use Existing</p>
                <p className="text-[10px] text-zinc-500">Arrange the 8 built-in recipes smartly</p>
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="border border-red-900/50 bg-red-950/30 p-3 text-sm text-red-400" data-testid="ai-error">{error}</div>
          )}
        </div>

        <div className="p-5 border-t border-zinc-800">
          <button onClick={handleGenerate} disabled={generating}
            className="w-full py-3 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="generate-plan-btn">
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating Your Plan...</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Generate {calories} Cal {goal.charAt(0).toUpperCase() + goal.slice(1)} Plan</>
            )}
          </button>
          {generating && <p className="text-[10px] text-zinc-600 text-center mt-2 font-mono">AI is crafting your personalized meal plan. This takes 10-20 seconds...</p>}
        </div>
      </div>
    </div>
  );
}

function GroceryList({ days, recipes, customRecipes }) {
  const [checkedItems, setCheckedItems] = useState({});
  const toggleCheck = (key) => setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }));

  // Consolidate all ingredients across the week
  const allRecipesMap = {};
  recipes.forEach(r => { allRecipesMap[r.id] = r; });
  customRecipes.forEach(r => { allRecipesMap[r.id] = r; });

  const ingredientsByCategory = {};
  Object.values(days).forEach(dayMeals => {
    dayMeals.forEach(meal => {
      const rid = meal.custom_recipe_id || meal.recipe_id;
      if (!rid) return;
      const recipe = allRecipesMap[rid];
      if (!recipe) return;
      const cat = recipe.category || 'Other';
      if (!ingredientsByCategory[cat]) ingredientsByCategory[cat] = new Set();
      recipe.ingredients?.forEach(ing => ingredientsByCategory[cat].add(ing));
    });
  });

  const CAT_COLORS = { Breakfast: '#f59e0b', Lunch: '#22c55e', Dinner: '#E63946', Snack: '#8b5cf6', Other: '#3b82f6' };
  const totalItems = Object.values(ingredientsByCategory).reduce((sum, s) => sum + s.size, 0);
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;

  return (
    <div data-testid="grocery-list-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl text-white uppercase tracking-wider">Grocery List</h3>
        <span className="text-xs text-zinc-500 font-mono">{checkedCount}/{totalItems} items</span>
      </div>
      <div className="space-y-4">
        {Object.entries(ingredientsByCategory).map(([cat, items]) => (
          <div key={cat}>
            <p className="text-xs font-heading uppercase tracking-widest mb-2" style={{ color: CAT_COLORS[cat] || '#3b82f6' }}>{cat}</p>
            <div className="space-y-1">
              {Array.from(items).map(item => {
                const key = `${cat}-${item}`;
                return (
                  <button key={item} onClick={() => toggleCheck(key)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left border transition-all ${checkedItems[key] ? 'border-zinc-800/30 bg-zinc-900/20 line-through text-zinc-700' : 'border-zinc-800/50 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700'}`}
                    data-testid={`grocery-item-${item.slice(0, 15).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                    <span className={`w-4 h-4 border rounded-sm flex items-center justify-center flex-shrink-0 ${checkedItems[key] ? 'bg-[#22c55e] border-[#22c55e]' : 'border-zinc-700'}`}>
                      {checkedItems[key] && <Check className="w-3 h-3 text-black" />}
                    </span>
                    {item}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {totalItems === 0 && <p className="text-zinc-600 text-sm">Add meals to your plan to see the grocery list.</p>}
      </div>
    </div>
  );
}

export default function MealPlannerPage() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [days, setDays] = useState({});
  const [customRecipes, setCustomRecipes] = useState([]);
  const [planName, setPlanName] = useState('My Meal Plan');
  const [planId, setPlanId] = useState(null);
  const [shareId, setShareId] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [activeDay, setActiveDay] = useState('Monday');
  const [pickerOpen, setPickerOpen] = useState(null); // { day, slotIndex }
  const [addRecipeOpen, setAddRecipeOpen] = useState(false);
  const [showGrocery, setShowGrocery] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSavedPlans, setShowSavedPlans] = useState(false);
  const [isSharedView, setIsSharedView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const printRef = useRef(null);

  // Load recipes and default plan
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const sharedPlanId = searchParams.get('plan');
        if (sharedPlanId) {
          const { data } = await mealPlansApi.getShared(sharedPlanId);
          setDays(data.days || {});
          setCustomRecipes(data.custom_recipes || []);
          setPlanName(data.name || 'Shared Plan');
          setShareId(data.share_id);
          setIsSharedView(true);
        } else {
          const { data } = await mealPlansApi.getDefaultPlan();
          setDays(data.days || {});
        }
        const recipeRes = await mealPlansApi.getRecipes();
        setRecipes(recipeRes.data || []);
      } catch (err) {
        console.error('Failed to load meal planner:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [searchParams]);

  // Load saved plans if logged in
  useEffect(() => {
    if (!user || !token) return;
    mealPlansApi.getAll(token).then(r => setSavedPlans(r.data || [])).catch(() => {});
  }, [user, token]);

  const getRecipe = useCallback((id) => {
    if (!id) return null;
    const found = recipes.find(r => r.id === id);
    if (found) return found;
    return customRecipes.find(r => r.id === id) || null;
  }, [recipes, customRecipes]);

  const updateSlot = (day, slotIndex, recipe) => {
    setDays(prev => {
      const dayMeals = [...(prev[day] || [])];
      if (recipe === null) {
        dayMeals[slotIndex] = { ...dayMeals[slotIndex], recipe_id: null, custom_recipe_id: null };
      } else if (recipe._custom) {
        dayMeals[slotIndex] = { ...dayMeals[slotIndex], recipe_id: null, custom_recipe_id: recipe.id };
      } else {
        dayMeals[slotIndex] = { ...dayMeals[slotIndex], recipe_id: recipe.id, custom_recipe_id: null };
      }
      return { ...prev, [day]: dayMeals };
    });
  };

  const updateServings = (day, slotIndex, delta) => {
    setDays(prev => {
      const dayMeals = [...(prev[day] || [])];
      const current = dayMeals[slotIndex]?.servings || 1;
      dayMeals[slotIndex] = { ...dayMeals[slotIndex], servings: Math.max(1, current + delta) };
      return { ...prev, [day]: dayMeals };
    });
  };

  const handleAddCustomRecipe = (recipe) => {
    setCustomRecipes(prev => [...prev, recipe]);
  };

  const handleSave = async () => {
    if (!user || !token) return;
    setSaving(true);
    try {
      const payload = { name: planName, days, custom_recipes: customRecipes };
      if (planId) {
        const { data } = await mealPlansApi.update(planId, payload, token);
        setShareId(data.share_id);
      } else {
        const { data } = await mealPlansApi.create(payload, token);
        setPlanId(data.id);
        setShareId(data.share_id);
      }
      const plansRes = await mealPlansApi.getAll(token);
      setSavedPlans(plansRes.data || []);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadPlan = async (savedPlan) => {
    if (!token) return;
    try {
      const { data } = await mealPlansApi.getById(savedPlan.id, token);
      setDays(data.days || {});
      setCustomRecipes(data.custom_recipes || []);
      setPlanName(data.name);
      setPlanId(data.id);
      setShareId(data.share_id);
      setIsSharedView(false);
      setShowSavedPlans(false);
    } catch (err) {
      console.error('Failed to load plan:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!token) return;
    try {
      await mealPlansApi.delete(id, token);
      setSavedPlans(prev => prev.filter(p => p.id !== id));
      if (planId === id) { setPlanId(null); setShareId(null); }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleShare = async () => {
    if (!shareId && user && token) await handleSave();
    if (shareId) {
      const url = `${window.location.origin}/tools/meal-planner?plan=${shareId}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAiGenerate = (result) => {
    if (result.days) setDays(result.days);
    if (result.custom_recipes) setCustomRecipes(prev => [...prev, ...result.custom_recipes]);
    if (result.plan_name) setPlanName(result.plan_name);
    setPlanId(null);
    setShareId(null);
    setIsSharedView(false);
  };

  // Calculate daily/weekly macros
  const dayMeals = days[activeDay] || [];
  const dailyProtein = dayMeals.reduce((sum, m) => {
    const r = getRecipe(m.custom_recipe_id || m.recipe_id);
    return sum + (r ? r.protein * (m.servings || 1) : 0);
  }, 0);
  const dailyCalories = dayMeals.reduce((sum, m) => {
    const r = getRecipe(m.custom_recipe_id || m.recipe_id);
    return sum + (r ? r.calories * (m.servings || 1) : 0);
  }, 0);

  const weeklyProtein = DAYS.reduce((sum, day) => {
    return sum + (days[day] || []).reduce((ds, m) => {
      const r = getRecipe(m.custom_recipe_id || m.recipe_id);
      return ds + (r ? r.protein * (m.servings || 1) : 0);
    }, 0);
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-zinc-500 text-sm font-mono">Loading meal planner...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-300 print:bg-white print:text-black" ref={printRef} data-testid="meal-planner-page">
      {/* Header */}
      <section className="pt-24 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Utensils className="w-5 h-5 text-[#22c55e]" />
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-[#22c55e] border border-[#22c55e]/30 px-3 py-1">Meal Planner</span>
            {isSharedView && <span className="text-[10px] font-mono text-zinc-500 border border-zinc-800 px-2 py-1">SHARED PLAN</span>}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h1 className="sr-only">Meal Planner — NinjAthlete</h1>
              <input
                value={planName}
                onChange={e => setPlanName(e.target.value)}
                className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white bg-transparent border-none outline-none w-full max-w-md print:text-black"
                data-testid="plan-name-input"
              />
              <p className="text-zinc-500 text-sm mt-1">Customize your meals, adjust servings, and generate a grocery list.</p>
            </div>
            <div className="flex gap-2 print:hidden flex-shrink-0">
              <button onClick={() => setAiModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 border border-[#22c55e]/40 text-[#22c55e] text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/10 hover:border-[#22c55e] transition-all"
                data-testid="ai-generate-btn"><Sparkles className="w-3 h-3" /> AI Plan</button>
              <button onClick={() => setAddRecipeOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 border border-zinc-800 text-zinc-400 text-xs font-heading uppercase tracking-wider hover:border-zinc-600 hover:text-white transition-all"
                data-testid="add-recipe-btn"><Plus className="w-3 h-3" /> Recipe</button>
              {user ? (
                <>
                  <button onClick={() => setShowSavedPlans(!showSavedPlans)}
                    className="flex items-center gap-1.5 px-3 py-2 border border-zinc-800 text-zinc-400 text-xs font-heading uppercase tracking-wider hover:border-zinc-600 hover:text-white transition-all"
                    data-testid="load-plans-btn"><BookOpen className="w-3 h-3" /> Plans</button>
                  <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-2 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors disabled:opacity-50"
                    data-testid="save-plan-btn"><Save className="w-3 h-3" /> {saving ? 'Saving...' : planId ? 'Update' : 'Save'}</button>
                </>
              ) : (
                <Link to="/auth" className="flex items-center gap-1.5 px-3 py-2 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors" data-testid="login-to-save">
                  <LogIn className="w-3 h-3" /> Login to Save
                </Link>
              )}
              {shareId && (
                <button onClick={handleShare}
                  className={`flex items-center gap-1.5 px-3 py-2 border text-xs font-heading uppercase tracking-wider transition-all ${copied ? 'border-[#22c55e] text-[#22c55e]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white'}`}
                  data-testid="share-plan-btn">{copied ? <Check className="w-3 h-3" /> : <Share2 className="w-3 h-3" />} {copied ? 'Copied!' : 'Share'}</button>
              )}
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 px-3 py-2 border border-zinc-800 text-zinc-400 text-xs font-heading uppercase tracking-wider hover:border-zinc-600 hover:text-white transition-all"
                data-testid="print-plan-btn"><Printer className="w-3 h-3" /> Print</button>
            </div>
          </div>
        </div>
      </section>

      {/* Saved Plans Dropdown */}
      {showSavedPlans && savedPlans.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 mb-4 print:hidden">
          <div className="border border-zinc-800 bg-zinc-900/60 p-3" data-testid="saved-plans-dropdown">
            <p className="text-xs font-heading uppercase tracking-wider text-zinc-500 mb-2">Your Saved Plans</p>
            <div className="space-y-1">
              {savedPlans.map(p => (
                <div key={p.id} className="flex items-center justify-between p-2 hover:bg-zinc-800/40 transition-colors">
                  <button onClick={() => handleLoadPlan(p)} className="text-sm text-white hover:text-[#22c55e] transition-colors text-left flex-1" data-testid={`load-plan-${p.id}`}>{p.name}</button>
                  <button onClick={() => handleDelete(p.id)} className="text-zinc-600 hover:text-red-500 transition-colors ml-2" data-testid={`delete-plan-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 pb-20">
        {/* Weekly overview bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="weekly-overview">
          <div className="border border-zinc-800 p-4">
            <p className="text-2xl font-heading text-white leading-none mb-1">{dailyProtein}g</p>
            <p className="text-[10px] font-mono tracking-wider uppercase text-[#22c55e]">Today's Protein</p>
          </div>
          <div className="border border-zinc-800 p-4">
            <p className="text-2xl font-heading text-white leading-none mb-1">{dailyCalories}</p>
            <p className="text-[10px] font-mono tracking-wider uppercase text-[#f59e0b]">Today's Calories</p>
          </div>
          <div className="border border-zinc-800 p-4">
            <p className="text-2xl font-heading text-white leading-none mb-1">{Math.round(weeklyProtein / 7)}g</p>
            <p className="text-[10px] font-mono tracking-wider uppercase text-[#3b82f6]">Avg Daily Protein</p>
          </div>
          <div className="border border-zinc-800 p-4 cursor-pointer hover:border-[#22c55e]/50 transition-colors print:hidden" onClick={() => setShowGrocery(!showGrocery)}>
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-[#22c55e]" />
              <p className="text-sm font-heading text-white uppercase">{showGrocery ? 'Hide' : 'View'} Grocery List</p>
            </div>
            <p className="text-[10px] font-mono text-zinc-500 mt-1">Tap to {showGrocery ? 'hide' : 'expand'}</p>
          </div>
        </div>

        {/* Grocery list (expandable) */}
        {showGrocery && (
          <div className="mb-8 border border-zinc-800 p-5 bg-zinc-900/40">
            <GroceryList days={days} recipes={recipes} customRecipes={customRecipes} />
          </div>
        )}

        {/* Day tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-hide print:hidden">
          {DAYS.map(day => {
            const dayProts = (days[day] || []).reduce((sum, m) => {
              const r = getRecipe(m.custom_recipe_id || m.recipe_id);
              return sum + (r ? r.protein * (m.servings || 1) : 0);
            }, 0);
            return (
              <button key={day} onClick={() => setActiveDay(day)}
                className={`px-3 py-2 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap border transition-all flex flex-col items-center gap-0.5 ${activeDay === day ? 'border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e]' : 'border-zinc-800/50 text-zinc-600 hover:text-zinc-400'}`}
                data-testid={`planner-day-${day.toLowerCase()}`}>
                <span>{day.slice(0, 3)}</span>
                {dayProts > 0 && <span className="text-[9px] font-mono">{dayProts}g</span>}
              </button>
            );
          })}
        </div>

        {/* Day meal grid */}
        <div className="space-y-3" data-testid="day-meal-grid">
          {dayMeals.map((meal, idx) => {
            const slotInfo = SLOTS[idx] || SLOTS[0];
            const recipe = getRecipe(meal.custom_recipe_id || meal.recipe_id);
            return (
              <div key={meal.servings} className="border border-zinc-800 bg-zinc-900/40 p-4 flex items-center gap-4" data-testid={`meal-slot-${slotInfo.id}`}>
                <div className="w-1.5 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: slotInfo.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-heading uppercase tracking-widest mb-1" style={{ color: slotInfo.color }}>{slotInfo.label}</p>
                  {recipe ? (
                    <div>
                      <p className="text-white text-sm font-bold truncate">{recipe.name}</p>
                      <div className="flex gap-3 text-[10px] text-zinc-500 mt-0.5">
                        <span className="flex items-center gap-0.5"><Beef className="w-3 h-3" />{recipe.protein * (meal.servings || 1)}g protein</span>
                        <span className="flex items-center gap-0.5"><Flame className="w-3 h-3" />{recipe.calories * (meal.servings || 1)} cal</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-zinc-600 text-sm italic">No meal selected</p>
                  )}
                </div>

                <div className="flex items-center gap-2 print:hidden flex-shrink-0">
                  {/* Servings adjuster */}
                  {recipe && (
                    <div className="flex items-center gap-1 border border-zinc-800 rounded">
                      <button onClick={() => updateServings(activeDay, idx, -1)} className="p-1 text-zinc-500 hover:text-white" data-testid={`servings-minus-${idx}`}><Minus className="w-3 h-3" /></button>
                      <span className="text-xs font-mono text-white w-5 text-center" data-testid={`servings-count-${idx}`}>{meal.servings || 1}</span>
                      <button onClick={() => updateServings(activeDay, idx, 1)} className="p-1 text-zinc-500 hover:text-white" data-testid={`servings-plus-${idx}`}><Plus className="w-3 h-3" /></button>
                    </div>
                  )}
                  {/* Change recipe */}
                  <button onClick={() => setPickerOpen({ day: activeDay, slotIndex: idx })}
                    className="p-2 border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600 transition-all"
                    data-testid={`change-recipe-${idx}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}

          {dayMeals.length === 0 && (
            <div className="border border-dashed border-zinc-800 p-8 text-center">
              <p className="text-zinc-600 text-sm">No meals for {activeDay}. Add slots from the default plan.</p>
            </div>
          )}
        </div>

        {/* Macro Tracker CTA */}
        <div className="mt-8 border border-[#22c55e]/20 bg-[#22c55e]/5 p-5 print:hidden" data-testid="macro-tracker-cta">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-[#22c55e] flex-shrink-0" />
              <div>
                <p className="text-white text-sm font-bold">Track Your Macros</p>
                <p className="text-zinc-500 text-[11px]">Log what you eat, see compliance vs your plan, and track weekly trends.</p>
              </div>
            </div>
            <Link to="/tools/macro-tracker" className="flex items-center gap-2 px-4 py-2 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors whitespace-nowrap" data-testid="open-macro-tracker-btn">
              Open Tracker <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* CTA back to article */}
        <div className="mt-12 border border-zinc-800 border-dashed p-6 text-center print:hidden">
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-600 mb-2">Need Inspiration?</p>
          <Link to="/blogs/article/high-protein-meal-prep-guide" className="inline-flex items-center gap-2 text-xs font-mono text-[#22c55e] hover:underline" data-testid="back-to-article-link">
            Read the Full Meal Prep Guide <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </main>

      {/* Print styles */}
      <style>{`
        @media print {
          nav, .print\\:hidden, footer, button { display: none !important; }
          body { background: white !important; }
          * { color: black !important; border-color: #ddd !important; background-color: white !important; }
          .font-heading { font-family: Arial, sans-serif !important; }
        }
      `}</style>

      {/* AI Generate Modal */}
      {aiModalOpen && (
        <AiGenerateModal onGenerate={handleAiGenerate} onClose={() => setAiModalOpen(false)} />
      )}

      {/* Recipe Picker Modal */}
      {pickerOpen && (
        <RecipePicker
          recipes={recipes}
          customRecipes={customRecipes.map(r => ({ ...r, _custom: true }))}
          slot={pickerOpen}
          onSelect={(recipe) => updateSlot(pickerOpen.day, pickerOpen.slotIndex, recipe)}
          onClose={() => setPickerOpen(null)}
        />
      )}

      {/* Add Custom Recipe Modal */}
      {addRecipeOpen && (
        <AddRecipeModal onAdd={handleAddCustomRecipe} onClose={() => setAddRecipeOpen(false)} />
      )}
    </div>
  );
}
